import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "vendor" ? "vendor" : "customer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState((params.get("ref") || "").toUpperCase());
  // Iter 67eg — GDPR signup consent. UNCHECKED by default so opt-in is
  // affirmative. Sent to the backend, stored in audit_log + user doc.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { name, email, password, role };
    if (role === "vendor") {
      payload.business_name = businessName;
      payload.phone = phone;
    }
    if (role === "customer" && referralCode.trim()) {
      payload.referral_code = referralCode.trim().toUpperCase();
    }
    // Iter 67ej — vendor signup also accepts a referrer code (vendor→vendor).
    // Same payload key; backend differentiates by the new vendor's role.
    if (role === "vendor" && referralCode.trim()) {
      payload.referral_code = referralCode.trim().toUpperCase();
    }
    // Iter 67eg — record consent intent. Backend uses this to seed
    // `marketing_consent` + a signup audit-log entry.
    payload.marketing_consent = !!marketingConsent;
    const res = await register(payload);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    // Iter 67eg — analytics breadcrumb. Dispatched BEFORE the navigate so
    // any in-page tracker (GA / PostHog / Mixpanel — wired up via a window
    // listener elsewhere) captures the event before the route changes.
    // Defensive try/catch because analytics must never block signup.
    try {
      window.dispatchEvent(new CustomEvent("analytics:track", {
        detail: {
          event: "signup_marketing_consent",
          value: !!marketingConsent,
          role,
        },
      }));
    } catch { /* analytics is best-effort */ }
    toast.success("Account created");
    if (role === "vendor") {
      toast.info("Your vendor account is pending admin approval");
      navigate("/dashboard/vendor");
    } else {
      navigate("/dashboard/customer");
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-2">Create your account</h1>
        <p className="text-neutral-600 mb-8 text-sm">Join RentalCar.al as a customer or vendor.</p>

        <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-neutral-100 mb-6">
          <button type="button" onClick={() => setRole("customer")} className={`py-2 rounded-full text-sm font-medium transition ${role === "customer" ? "bg-white shadow-sm" : "text-neutral-600"}`} data-testid="register-role-customer">I'm renting</button>
          <button type="button" onClick={() => setRole("vendor")} className={`py-2 rounded-full text-sm font-medium transition ${role === "vendor" ? "bg-white shadow-sm" : "text-neutral-600"}`} data-testid="register-role-vendor">I'm hosting</button>
        </div>

        {role === "customer" && (
          <>
            <GoogleSignInButton label="Sign up with Google" />
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs uppercase tracking-widest text-neutral-500">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl" data-testid="register-name" />
          </div>
          {role === "vendor" && (
            <>
              <div>
                <Label>Business name</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="mt-1 rounded-xl" data-testid="register-business" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-xl" data-testid="register-phone" />
              </div>
            </>
          )}
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-xl" data-testid="register-email" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-xl" data-testid="register-password" />
          </div>
          {role === "customer" && (
            <div>
              <Label className="flex items-center gap-1.5">
                Referral code <span className="text-[10px] font-medium uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">€10 off</span>
              </Label>
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Optional — e.g. ALICE-AB12"
                className="mt-1 rounded-xl uppercase tracking-wider font-mono"
                data-testid="register-referral-code"
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                Got a code from a friend? You'll both earn 1,000 points (€10) on your first confirmed trip.
              </div>
            </div>
          )}
          {/* Iter 67ej — Vendor referral entry. Same field, different
              reward: 1 month commission-free for the referrer once you
              list ≥3 active cars. */}
          {role === "vendor" && (
            <div>
              <Label className="flex items-center gap-1.5">
                Referred by another vendor? <span className="text-[10px] font-medium uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">1 month free</span>
              </Label>
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Optional — e.g. ROMA-X4F7"
                className="mt-1 rounded-xl uppercase tracking-wider font-mono"
                data-testid="register-vendor-referral-code"
              />
              <div className="text-[11px] text-neutral-500 mt-1">
                Was a fellow rental company referred you? They'll earn 1 month commission-free once you've listed at least 3 active cars.
              </div>
            </div>
          )}

          {/* Iter 67eg — GDPR consent. UNCHECKED by default so opt-in is
              affirmative. The unsubscribe link in every broadcast email
              gives users an out-of-band way to flip this later. */}
          <label
            className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl border border-amber-200 bg-amber-50/40 p-3"
            data-testid="register-marketing-card"
          >
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              data-testid="register-marketing-consent"
            />
            <span className="text-xs leading-snug text-neutral-700">
              <strong className="text-neutral-900">Send me marketing emails</strong> with seasonal deals,
              new destinations, and platform updates. You can unsubscribe at any time from any email.
              Booking confirmations, receipts and damage reports will always be sent regardless of this choice.
            </span>
          </label>

          <Button type="submit" disabled={loading} className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-center text-neutral-500 mt-6">
          Already have an account? <Link to="/login" className="text-blue-600 font-medium">Log in</Link>
        </p>
      </div>
    </div>
  );
}
